import { Node, mergeAttributes } from "@tiptap/core";
import { Fragment, Slice } from "@tiptap/pm/model";

export interface AgentSuggestionBlockAttrs {
    editId: string;
    rationale: string;
    /**
     * 'insert' — new blocks added; on reject, just delete the wrapper.
     * 'replace' — anchor block is being replaced; on reject, restore original via the
     *             agent_pending_replace mark on the anchor (handled in AgentSuggestionExtension).
     */
    mode: "insert" | "replace";
    /**
     * editId of the anchor mark this block pairs with (for 'replace' mode).
     * Empty for 'insert' mode.
     */
    anchorEditId: string;
    /**
     * Plain text of the list item after which new items should be inserted
     * (for insert-after-list-item mode). When set, accept splices new list
     * items into the preceding list directly after the matching item rather
     * than unwrapping the block in place (which would put items outside the
     * list). Empty when not applicable.
     */
    insertAnchorText: string;
}

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        agentSuggestionBlock: {
            acceptSuggestionBlock: (editId: string) => ReturnType;
            rejectSuggestionBlock: (editId: string) => ReturnType;
        };
    }
}

/**
 * Block-level node that wraps multi-block agent-proposed content for staging.
 *
 * Layout (rendered by the toDOM below; styled via TiptapEditor.scss):
 *   <div data-agent-suggestion-block data-edit-id=... data-mode=...>
 *     <div data-agent-suggestion-block-header contenteditable=false>
 *       <span class="sx-block-label">Proposed insertion</span>
 *       <span class="sx-block-rationale">{rationale}</span>
 *       <button data-suggestion-accept data-edit-id=...>Accept</button>
 *       <button data-suggestion-reject data-edit-id=...>Reject</button>
 *     </div>
 *     {content blocks parsed from new_string}
 *   </div>
 *
 * The wrapper renders with a green left border + faded green background so the
 * user can see the proposed content inline before deciding. Accept unwraps the
 * wrapper (its children take its place at the top level); reject removes the
 * wrapper entirely and clears any paired pending-replace mark on the anchor.
 *
 * Click delegation is handled at the editor box level (TiptapEditor.handleEditorMouseDown):
 * clicking a button with data-suggestion-accept / data-suggestion-reject dispatches
 * to the adapter's resolve(editId, status), which then calls accept/reject commands.
 *
 * Forward-compatibility note: the content type is `block+`, so any future block
 * kind the editor supports (mermaid, tables, future tool-fetched widgets) can be
 * staged inside this wrapper without further schema changes. External tools that
 * fetch content to inject can simply return markdown; the adapter parses it and
 * wraps it here.
 */
export const AgentSuggestionBlock = Node.create({
    name: "agent_suggestion_block",
    group: "block",
    content: "block+",
    defining: true,
    isolating: true,

    addAttributes() {
        return {
            editId: { default: "" },
            rationale: { default: "" },
            mode: { default: "insert" },
            anchorEditId: { default: "" },
            insertAnchorText: { default: "" }
        };
    },

    parseHTML() {
        return [
            {
                tag: "div[data-agent-suggestion-block]",
                getAttrs: (el): AgentSuggestionBlockAttrs | false => {
                    if (!(el instanceof HTMLElement)) return false;
                    return {
                        editId: el.getAttribute("data-edit-id") ?? "",
                        rationale: el.getAttribute("data-rationale") ?? "",
                        mode: (el.getAttribute("data-mode") as "insert" | "replace") ?? "insert",
                        anchorEditId: el.getAttribute("data-anchor-edit-id") ?? "",
                        insertAnchorText: el.getAttribute("data-insert-anchor-text") ?? ""
                    };
                }
            }
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        const editId = String(node.attrs.editId ?? "");
        const rationale = String(node.attrs.rationale ?? "");
        const mode = String(node.attrs.mode ?? "insert");
        const label = mode === "replace" ? "Proposed replacement" : "Proposed insertion";

        // Use a render array so PM still owns the children rendering (the `0`
        // sentinel — see prosemirror-model docs). The header is a sibling
        // contenteditable=false node so clicks don't move the caret into it.
        const insertAnchorText = String(node.attrs.insertAnchorText ?? "");
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-agent-suggestion-block": "true",
                "data-edit-id": editId,
                "data-mode": mode,
                "data-anchor-edit-id": String(node.attrs.anchorEditId ?? ""),
                "data-rationale": rationale,
                ...(insertAnchorText ? { "data-insert-anchor-text": insertAnchorText } : {}),
                class: "sx-suggestion-block"
            }),
            [
                "div",
                {
                    "data-agent-suggestion-block-header": "true",
                    contenteditable: "false",
                    class: "sx-suggestion-block-header"
                },
                ["span", { class: "sx-block-label" }, label],
                rationale ? ["span", { class: "sx-block-rationale" }, rationale] : ["span", { class: "sx-block-rationale sx-block-rationale-empty" }, ""],
                [
                    "button",
                    {
                        type: "button",
                        class: "sx-block-accept",
                        "data-suggestion-accept": "true",
                        "data-edit-id": editId,
                        "aria-label": "Accept proposed change"
                    },
                    "Accept"
                ],
                [
                    "button",
                    {
                        type: "button",
                        class: "sx-block-reject",
                        "data-suggestion-reject": "true",
                        "data-edit-id": editId,
                        "aria-label": "Reject proposed change"
                    },
                    "Reject"
                ]
            ],
            ["div", { class: "sx-suggestion-block-body" }, 0]
        ];
    },

    addCommands() {
        return {
            /**
             * Accept the suggestion block identified by `editId`:
             *   • 'insert' mode: unwrap the block in place (its children become
             *      top-level blocks where the wrapper was).
             *   • 'replace' mode: locate the paired agent_pending_replace mark
             *      via attrs.anchorEditId, delete the marked range's parent
             *      block, then unwrap this suggestion block.
             *
             * Both paths leave the doc with the proposed content as ordinary
             * editable blocks — no trace of staging metadata.
             */
            acceptSuggestionBlock:
                (editId: string) =>
                ({ tr, state, dispatch }) => {
                    let wrapperPos = -1;
                    let wrapperNode: any = null;
                    state.doc.descendants((node, pos) => {
                        if (wrapperPos !== -1) return false;
                        if (node.type.name !== "agent_suggestion_block") return true;
                        if (node.attrs.editId !== editId) return true;
                        wrapperPos = pos;
                        wrapperNode = node;
                        return false;
                    });
                    if (wrapperPos === -1 || !wrapperNode) return false;

                    const anchorEditId = String(wrapperNode.attrs.anchorEditId ?? "");
                    const mode = String(wrapperNode.attrs.mode ?? "insert");

                    if (mode === "replace" && anchorEditId) {
                        const range = findMarkedBlockRange(state, "agent_pending_replace", anchorEditId);
                        if (range) {
                            // Check if the anchor paragraph lives inside a list item.
                            // If so, replace the whole listItem with the suggestion content
                            // (wrapped in new listItems) rather than the old paragraph-delete
                            // + in-place-unwrap approach, which left an empty item and put
                            // the replacement content after the list instead of inside it.
                            const listItemRange = expandToListItemAndRemainder(state.doc, range.from);
                            if (listItemRange) {
                                const listItemType = state.schema.nodes.listItem ?? state.schema.nodes.list_item;

                                // Delete suggestion block first — it's after the list so
                                // positions of the listItem are unaffected by this step.
                                tr.delete(wrapperPos, wrapperPos + wrapperNode.nodeSize);

                                let replacementFrag: any;
                                if (listItemType) {
                                    const items: any[] = [];
                                    wrapperNode.content.forEach((block: any) => {
                                        // If the block is itself a list, inline its listItem
                                        // children rather than creating a nested list.
                                        if (
                                            block.type.name === "orderedList" ||
                                            block.type.name === "bulletList" ||
                                            block.type.name === "ordered_list" ||
                                            block.type.name === "bullet_list"
                                        ) {
                                            block.forEach((item: any) => items.push(item));
                                        } else {
                                            items.push(listItemType.create(null, block));
                                        }
                                    });
                                    replacementFrag = Fragment.from(items.length > 0 ? items : [listItemType.create()]);
                                } else {
                                    replacementFrag = wrapperNode.content;
                                }

                                // Replace from the anchor list item through the end of the
                                // parent list. This removes all items from the anchor onward
                                // (including any that the agent included verbatim in new_string
                                // as renumbered copies) so they don't remain as duplicates.
                                tr.replace(listItemRange.from, listItemRange.to, new Slice(replacementFrag, 0, 0));
                                if (dispatch) dispatch(tr);
                                return true;
                            }

                            // Non-list anchor: original behaviour — delete the anchor
                            // paragraph then unwrap the suggestion block in place.
                            tr.delete(range.from, range.to);
                            if (wrapperPos > range.from) {
                                wrapperPos -= range.to - range.from;
                            }
                        }
                    }

                    // Insert-in-list mode: the suggestion block was staged after a
                    // list using "insert after" semantics. Splice the new list items
                    // directly into the preceding list after the anchor item rather
                    // than unwrapping in place (which would produce a second list).
                    const insertAnchorText = String(wrapperNode.attrs.insertAnchorText ?? "");
                    if (mode === "insert" && insertAnchorText) {
                        const $wrapper = state.doc.resolve(wrapperPos);
                        const preceding = $wrapper.nodeBefore;
                        const isList =
                            preceding?.type.name === "orderedList" ||
                            preceding?.type.name === "bulletList" ||
                            preceding?.type.name === "ordered_list" ||
                            preceding?.type.name === "bullet_list";
                        if (preceding && isList) {
                            const listStart = wrapperPos - preceding.nodeSize;
                            // Find the list item whose plain text matches the anchor.
                            let anchorItemEnd: number | null = null;
                            preceding.forEach((item: any, offset: number) => {
                                let itemText = "";
                                item.descendants((child: any) => {
                                    if (child.isText) itemText += child.text;
                                    return true;
                                });
                                if (itemText.trim() === insertAnchorText.trim()) {
                                    // listStart + 1 skips the list's opening token;
                                    // offset is the item's position within list content.
                                    anchorItemEnd = listStart + 1 + offset + item.nodeSize;
                                }
                            });
                            if (anchorItemEnd !== null) {
                                const listItemType =
                                    state.schema.nodes.listItem ?? state.schema.nodes.list_item;
                                const items: any[] = [];
                                wrapperNode.content.forEach((block: any) => {
                                    if (
                                        block.type.name === "orderedList" ||
                                        block.type.name === "bulletList" ||
                                        block.type.name === "ordered_list" ||
                                        block.type.name === "bullet_list"
                                    ) {
                                        block.forEach((item: any) => items.push(item));
                                    } else if (listItemType) {
                                        items.push(listItemType.create(null, block));
                                    }
                                });
                                if (items.length > 0) {
                                    // Delete wrapper first — it's after the list so the
                                    // list's positions (anchorItemEnd) are unaffected.
                                    tr.delete(wrapperPos, wrapperPos + wrapperNode.nodeSize);
                                    tr.insert(anchorItemEnd, Fragment.from(items));
                                    if (dispatch) dispatch(tr);
                                    return true;
                                }
                            }
                        }
                    }

                    // Unwrap: replace the wrapper node with its content.
                    const wrapperEnd = wrapperPos + wrapperNode.nodeSize;
                    tr.replaceWith(wrapperPos, wrapperEnd, wrapperNode.content);

                    if (dispatch) dispatch(tr);
                    return true;
                },

            /**
             * Reject: remove the suggestion block entirely AND clear the
             * pending-replace mark on the anchor (if mode='replace'). The
             * original document state is restored.
             */
            rejectSuggestionBlock:
                (editId: string) =>
                ({ tr, state, dispatch }) => {
                    let wrapperPos = -1;
                    let wrapperNode: any = null;
                    state.doc.descendants((node, pos) => {
                        if (wrapperPos !== -1) return false;
                        if (node.type.name !== "agent_suggestion_block") return true;
                        if (node.attrs.editId !== editId) return true;
                        wrapperPos = pos;
                        wrapperNode = node;
                        return false;
                    });
                    if (wrapperPos === -1 || !wrapperNode) return false;

                    const anchorEditId = String(wrapperNode.attrs.anchorEditId ?? "");
                    const wrapperEnd = wrapperPos + wrapperNode.nodeSize;
                    tr.delete(wrapperPos, wrapperEnd);

                    if (anchorEditId) {
                        const markType = state.schema.marks["agent_pending_replace"];
                        if (markType) {
                            // Remove the pending-replace mark from any node that
                            // carries this anchorEditId. We re-walk because
                            // positions inside tr.doc may have shifted.
                            tr.doc.descendants((node, pos) => {
                                const m = node.marks.find(mk => mk.type === markType && mk.attrs.editId === anchorEditId);
                                if (!m) return;
                                tr.removeMark(pos, pos + node.nodeSize, markType);
                            });
                        }
                    }

                    if (dispatch) dispatch(tr);
                    return true;
                }
        };
    }
});

interface MarkedBlockRange {
    from: number;
    to: number;
}

/**
 * Find the block containing the first text node with the given mark + editId.
 * Returns the range covering the WHOLE BLOCK (not just the marked text).
 * Used by acceptSuggestionBlock to delete the original block that a
 * suggestion is replacing.
 */
function findMarkedBlockRange(
    state: { doc: { descendants: (cb: (node: any, pos: number) => boolean | void) => void } },
    markName: string,
    editId: string
): MarkedBlockRange | null {
    let result: MarkedBlockRange | null = null;
    state.doc.descendants((node: any, pos: number) => {
        if (result) return false;
        if (!node.isTextblock) return true;
        let hasMatch = false;
        node.descendants((child: any) => {
            if (hasMatch) return false;
            if (!child.isText) return true;
            const m = child.marks.find((mk: any) => mk.type.name === markName && mk.attrs.editId === editId);
            if (m) hasMatch = true;
            return false;
        });
        if (hasMatch) {
            result = { from: pos, to: pos + node.nodeSize };
            return false;
        }
        return false;
    });
    return result;
}

/**
 * Walk up the resolved-position tree from `pos` and return the range of the
 * nearest listItem ancestor, or null if the position is not inside a list item.
 */
function expandToListItem(doc: any, pos: number): { from: number; to: number } | null {
    const $pos = doc.resolve(pos);
    for (let d = $pos.depth; d >= 1; d--) {
        const name = $pos.node(d).type.name;
        if (name === "listItem" || name === "list_item") {
            return { from: $pos.before(d), to: $pos.after(d) };
        }
    }
    return null;
}

/**
 * Like expandToListItem but extends `to` through the end of the parent list's
 * content. This covers the anchor item AND all items after it, so that on
 * accept the suggestion block's replacement items sweep out any trailing items
 * that the agent included verbatim (renumbered) in new_string — preventing
 * duplicates when the original list had items beyond the anchor.
 */
function expandToListItemAndRemainder(doc: any, pos: number): { from: number; to: number } | null {
    const $pos = doc.resolve(pos);
    for (let d = $pos.depth; d >= 1; d--) {
        const name = $pos.node(d).type.name;
        if (name === "listItem" || name === "list_item") {
            const listDepth = d - 1;
            if (listDepth < 1) return null;
            const parentName = $pos.node(listDepth).type.name;
            if (
                parentName !== "orderedList" &&
                parentName !== "bulletList" &&
                parentName !== "ordered_list" &&
                parentName !== "bullet_list"
            ) return null;
            // $pos.after(listDepth) is the absolute position immediately after
            // the parent list node. Subtract 1 to land on the last content
            // position inside the list (before its closing token).
            const listContentEnd = $pos.after(listDepth) - 1;
            return { from: $pos.before(d), to: listContentEnd };
        }
    }
    return null;
}
